import React, { FC, useCallback, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import orderBy from 'lodash/orderBy'
import take from 'lodash/take'
import uniqBy from 'lodash/uniqBy'
import { useGeocodingSearch } from 'transfix-maps/hooks/useGeocodingSearch'
import AutocompleteBase, {
  getAutocompleteOptions,
  IAutocompleteBaseOption,
  TAutocompleteBaseChangeHandler,
  TAutocompleteBaseSharedProps,
  useAutocompleteInputState,
  useAutocompleteState,
} from 'transfix-ui/components/AutocompleteBase'
import { ILocationSuggestion } from 'transfix-ui/components/SuggestLocation'
import { IGeocodingResultTypeEnum } from 'types/graphqlTypes'
import { useDebounce } from 'use-debounce/lib'
import { getFacilityLabelFromLocation } from 'apps/fleet-planner/components/atoms/FacilityAutosuggest'
import { IFacilityLocation, mapFacilityToOption } from 'apps/fleet-planner/utils/autosuggest'
import { selectRecentFacilities } from 'apps/fleet-planner/utils/selectors'
import { getEntityById } from 'utils/getEntityById'
import { IOptionGroupIndex } from './constants'
import { useDefaultValues } from './utils'

export type TFacilityLocationAutosuggestChangeHandler = (
  change: Partial<IFacilityLocation> | undefined
) => void

export interface IFacilityLocationAutosuggestProps
  extends Omit<TAutocompleteBaseSharedProps, 'inputIcon' | 'renderInputType'> {
  onChange: TFacilityLocationAutosuggestChangeHandler
  value?: IFacilityLocation
}

export const FacilityLocationAutosuggest: FC<IFacilityLocationAutosuggestProps> = ({
  onChange: onChangeProp,
  placeholder = 'Search by city, state, or facility',
  value: valueProp,
  ...props
}) => {
  const { defaultInputValue, defaultValue } = useDefaultValues(valueProp)

  // Mirrors canonical value, but triggers API calls, validation, filters
  const [inputValue, inputChangeHandler] = useAutocompleteInputState(defaultInputValue)
  const [value, setValue] = useAutocompleteState(defaultValue)

  const [inputSearch] = useDebounce(inputValue, 250)

  const { results: mapResults = [], getResults } = useGeocodingSearch({
    resultTypes: [IGeocodingResultTypeEnum.City],
    getResultLabel: ({ city, stateCode }) => {
      return [city, stateCode].filter(Boolean).join(', ')
    },
  })

  const results: ILocationSuggestion[] = useMemo(() => {
    // Make sure that current value is always selectable
    const currentValue = valueProp?.location?.id ? [valueProp.location] : []

    return uniqBy(
      [
        ...mapResults
          .filter(r => r?.label !== valueProp?.location?.label)
          .map(result => {
            const { stateCode } = result || {}
            return {
              ...result,
              state: stateCode ?? '',
            }
          }),
        ...currentValue,
      ],
      'label'
    )
  }, [mapResults, valueProp])

  const facilities = useSelector(selectRecentFacilities)

  const changeHandler = useCallback<TAutocompleteBaseChangeHandler>(
    (_event, value) => {
      const option = value as IAutocompleteBaseOption<Record<string, any>>
      const location = getEntityById(results, option?.id)
      const facility = getEntityById(facilities, option?.id)

      if (!option?.id) {
        // With no valid value, send undefined
        onChangeProp(undefined)
      } else if (location?.id) {
        onChangeProp({
          location,
        })
      } else if (facility?.id) {
        const { addresses = [], id } = facility || {}
        const [address] = addresses || []
        const { locality: city, postalCode: pc, subdivisionCode: state } = address || {}

        // Remove null value
        const postalCode = pc ?? undefined

        onChangeProp({
          facility: {
            city,
            state,
            postalCode,
            id,
          },
        })
      }

      setValue(value)
    },
    [facilities, onChangeProp, results, setValue]
  )

  // Update query search when input updates
  useEffect(() => {
    async function fetchResults() {
      await getResults(inputValue)
    }

    // Prevents fetch on mount
    if (!!inputSearch && inputSearch !== valueProp?.location?.label) {
      fetchResults()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputSearch])

  // This behavior is a bit too custom to integrate into Autosuggest,
  // so the filter has to happen before passing this value to component
  const filteredFacilities = useMemo(() => {
    const filtered = facilities.filter(facility => {
      const { name, addresses = [] } = facility || {}
      const [address] = addresses || []
      const { addressLine1, locality: city, postalCode, subdivisionCode: state } = address || {}

      const text = getFacilityLabelFromLocation(name, {
        addressLine1,
        city,
        state,
        postalCode: postalCode ?? '',
      })

      return text?.toLowerCase().includes(inputSearch)
    })

    // highest id values are most recent facilities
    const ordered = orderBy(filtered, ['id'], ['desc'])

    // return only 5 after filtering
    return take(ordered, 5)
  }, [facilities, inputSearch])

  const locationOptions = getAutocompleteOptions({
    inputValue,
    dividerBefore: true,
    optionType: 'withIcon',
    iconName: 'LocationPinIcon',
    groupIndex: IOptionGroupIndex.Location,
    groupName: 'City or State',
    options: results,
    mapOption: ({ id = '', label: text = '' }) => ({ id, text }),
  })

  const facilityOptions = getAutocompleteOptions({
    inputValue,
    dividerBefore: true,
    optionType: 'withIcon',
    iconName: 'LocationPinIcon',
    groupIndex: IOptionGroupIndex.Facility,
    groupName: inputValue ? 'Facilities' : 'Recent Facilities',
    options: filteredFacilities,
    mapOption: facility => mapFacilityToOption(facility),
  })

  const allOptions = useMemo(() => [...facilityOptions, ...locationOptions], [
    facilityOptions,
    locationOptions,
  ])

  return (
    <AutocompleteBase
      {...props}
      autoSelect={false}
      clearOnBlur={false}
      inputIcon='Search'
      inputValue={inputValue}
      options={allOptions}
      placeholder={placeholder}
      renderInputType='withIcon'
      value={value}
      onChange={changeHandler}
      onInputChange={inputChangeHandler}
    />
  )
}

export default FacilityLocationAutosuggest
